import { chartConfig } from '@/config/chart';
import { useAppDispatch, useAppSelector } from '@/state/hooks';
import { fetchUserMarkets } from '@/state/reducers/marketPairSlice';
import { IChartingLibraryWidget, LanguageCode } from '@/types/charting_library';
import { useEffect, useMemo, useRef } from 'react';
import { useLocale } from 'react-aria';
import { getLocalChartState, initChartSettings } from './helpers/chartHelper';
import { selectLanguage } from './state/reducers/languageSlice';
import { selectAppliedTheme } from './state/reducers/themeSlice';
import usePopOutCSSVariables from './utils/hooks/usePopOutCSSVariables';
import { useSimpleDatafeed } from './utils/hooks/useSimpleDatafeed';

const queryString = location.search;
const params = new URLSearchParams(queryString);
const symbol = params.get('symbol') || 'BTC/USD';

export default function Chart({ id = 'popout', isEnlarged = false }: any) {
    const dispatch = useAppDispatch();

    const { locale } = useLocale();
    const lang = useAppSelector(selectLanguage);
    const appliedTheme = useAppSelector(selectAppliedTheme);

    const chartId = useMemo(() => `tv_chart_${id}${isEnlarged ? '_enlarged' : ''}`, [id]);
    const elementId = useMemo(() => `tv_chart_container_${id}${isEnlarged ? '_enlarged' : ''}`, [id, isEnlarged]);

    const datafeed = useSimpleDatafeed(id, chartId, symbol);
    const variables = usePopOutCSSVariables();

    const widgetRef = useRef<IChartingLibraryWidget | null>(null);

    async function initChart() {
        window[chartId] = {};
        if (window[chartId]) window[chartId].chartLoaded = false;

        const chartState = getLocalChartState(elementId);
        if (chartState) {
            chartState.charts[0].panes[0].sources[0].state.symbol = symbol;
        } else {
            if (window[chartId]) window[chartId].chartLoaded = true;
        }

        const localization: LanguageCode =
            lang === 'en'
                ? 'en'
                : lang === 'dk'
                ? 'da_DK'
                : lang === 'zh'
                ? 'zh'
                : lang === 'ar'
                ? 'ar'
                : lang === 'es'
                ? 'es'
                : 'en';

        const TVWidget = new window.TradingView.widget(
            chartConfig('popout', symbol, localization, locale, datafeed, variables, appliedTheme)
        );
        if (window[chartId]) window[chartId].TVWidget = TVWidget;
        TVWidget.onChartReady(() => {
            widgetRef.current = TVWidget;
            if (chartState) {
                TVWidget.load(chartState);
                if (window[chartId]) window[chartId].chartLoaded = true;
            }
            initChartSettings(TVWidget, variables);
        });
    }

    useEffect(() => {
        if (!variables['--color-brand-background']) return;
        if (!params.get('symbol')) {
            history.replaceState({}, '', 'chart?symbol=BTC/USD');
        }

        dispatch(fetchUserMarkets()).then(initChart);
    }, [variables]);

    return <div id={elementId} className="h-full" />;
}
